import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChild,
  input,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  ControlContainer,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule } from 'primeng/paginator';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownOption } from '../controls';
import { PaginatorComponent } from '../paginator/paginator.component';
import { TableHeaderComponent } from './table-header/table-header.component';
import { SearchControls } from './table-header/table-header.model';
import { TableAction } from './table.model';

export const DEFAULT_PAGE_SIZE = 8;

@Component({
  selector: 'ui-table',
  standalone: true,
  imports: [
    TableModule,
    NgTemplateOutlet,
    ReactiveFormsModule,
    TableHeaderComponent,
    TooltipModule,
    NgClass,
    PaginatorModule,
    ButtonModule,
    PaginatorComponent,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<TData, TFilter extends string | number> {
  public readonly value = input.required<TData[]>();
  public readonly rowHover = input(false, { transform: booleanAttribute });
  public readonly globalFilterFields = input<string[]>([]);
  public readonly actions = input<TableAction<TData>[]>();
  public readonly paginatorLabelSuffix = input('');
  public readonly form = input.required<FormGroup<SearchControls<TFilter>>>();
  public readonly headerPlaceholder = input('');
  public readonly headerDropdownOptions = input<DropdownOption<unknown>[]>();

  protected readonly table = viewChild<Table>('table');

  protected readonly filterTrigger = signal(0);
  public readonly tableRef = computed(() => this.table());
  protected readonly isPaginatorVisible = computed(() => {
    this.filterTrigger();
    const table = this.table();
    const value = this.value();

    const total = table?.filteredValue?.length ?? value.length;

    return total > DEFAULT_PAGE_SIZE;
  });

  protected readonly DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE;

  @ContentChild('additionalCaption') additionalCaption?: TemplateRef<unknown>;
  @ContentChild('additionalBody')
  additionalBody?: TemplateRef<{ $implicit: TData }>;
  @ContentChild('additionalExpansion') additionalExpansion?: TemplateRef<{
    $implicit: TData;
  }>;

  protected onTableFilter(): void {
    this.filterTrigger.update((v) => v + 1);
  }
}
